import {
  ForgotPasswordConfirm,
  ForgotPasswordRequest,
  NewPasswordLogin,
  UsernamePasswordLogin,
  useToastNotifications,
} from "@/shared/coralreef";
import { viewWidth } from "csx";
import { Content } from "gls/lib";
import { useState } from "react";

const NEW_PASSWORD_REQUIRED = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";
const CONFIRM_RESET_WITH_CODE = "CONFIRM_RESET_PASSWORD_WITH_CODE";

type Step = "login" | "new-password" | "forgot-request" | "forgot-confirm";

export interface LoginPageProps {
  signIn(
    email: string,
    password: string,
  ): Promise<{ isSignedIn: boolean; nextStep?: { signInStep: string } }>;
  confirmSignIn(newPassword: string): Promise<{ isSignedIn: boolean }>;
  resetPassword(
    username: string,
  ): Promise<{ nextStep?: { resetPasswordStep: string } }>;
  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  onSuccess(): void;
}

export function LoginPage({
  signIn,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  onSuccess,
}: LoginPageProps) {
  const { addToast } = useToastNotifications();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("login");
  const [resetUsername, setResetUsername] = useState("");

  const goTo = (next: Step) => {
    setError(null);
    setInfo(null);
    setStep(next);
  };

  const onLoginPasswordSubmitHandler = (username: string, password: string) => {
    signIn(username, password)
      .then((r) => {
        if (r.isSignedIn) {
          onSuccess();
        } else if (r.nextStep?.signInStep === NEW_PASSWORD_REQUIRED) {
          setStep("new-password");
        }
      })
      .catch((e) => {
        addToast(e instanceof Error ? e.message : "Sign-in failed", "error");
      });
  };

  const onNewPasswordSubmitHandler = (newPassword: string) => {
    confirmSignIn(newPassword)
      .then((r) => {
        if (r.isSignedIn) onSuccess();
      })
      .catch((e) => {
        addToast(
          e instanceof Error ? e.message : "Failed to set new password",
          "error",
        );
      });
  };

  const onForgotRequestSubmitHandler = (username: string) => {
    setError(null);
    resetPassword(username)
      .then((r) => {
        if (r.nextStep?.resetPasswordStep === CONFIRM_RESET_WITH_CODE) {
          setResetUsername(username);
          goTo("forgot-confirm");
        } else {
          setInfo("Your password has been reset. Please log in.");
          setStep("login");
        }
      })
      .catch((e) => {
        setError(
          e instanceof Error ? e.message : "Failed to start password reset",
        );
      });
  };

  const onForgotConfirmSubmitHandler = (
    confirmationCode: string,
    newPassword: string,
  ) => {
    setError(null);
    confirmResetPassword(resetUsername, confirmationCode, newPassword)
      .then(() => {
        setInfo("Your password has been reset. Please log in.");
        setStep("login");
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to reset password");
      });
  };

  return (
    <Content
      width={viewWidth(100)}
      height="75vh"
      verticalAlign="center"
      horizontalAlign="center"
    >
      <div className="flex flex-col items-center gap-3">
        {step === "login" && (
          <UsernamePasswordLogin
            onSubmit={onLoginPasswordSubmitHandler}
            onForgotPassword={() => goTo("forgot-request")}
          />
        )}
        {step === "new-password" && (
          <NewPasswordLogin onSubmit={onNewPasswordSubmitHandler} />
        )}
        {step === "forgot-request" && (
          <ForgotPasswordRequest
            onSubmit={onForgotRequestSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {step === "forgot-confirm" && (
          <ForgotPasswordConfirm
            onSubmit={onForgotConfirmSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {info && (
          <div
            role="status"
            data-testid="info-message"
            className="text-sm w-[300px]"
          >
            {info}
          </div>
        )}
        {error && (
          <div
            role="alert"
            data-testid="error-message"
            className="text-red-500 text-sm w-[300px]"
          >
            {error}
          </div>
        )}
      </div>
    </Content>
  );
}
